import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { IoStatsChart } from "react-icons/io5";

import SelectProject from "@/components/selectProject";
import Modal from "@/components/modal";
import Loader from "@/components/loader";
import Table from "@/components/Table";
import Paginator from "@/components/Paginator";
import MultiSelect from "@/components/MultiSelect";

import api from "@/services/api";
import { timeSinceShort } from "@/utils";
import { ViewTabs, ColorLegend, getStatusColor, STATUS_COLORS, fmt, invoiceNumber, TIRANA_CUTOFF, isTirana } from "./shared";

const HEADER = [
  { title: "Index", key: "index" },
  { title: "Client", key: "projectName" },
  { title: "Sent date", key: "sentAt" },
  { title: "Due date", key: "dueAt" },
  { title: "Late since", key: "" },
  { title: "Total", key: "total" },
  { title: "Collection status", key: "collection_status" },
  { title: "Next action date", key: "nextActionDate" },
  { title: "Company", key: "organisation_name" },
];

// Severity order used to sort the status chips (worst last)
const STATUS_ORDER = Object.keys(STATUS_COLORS);
const statusRank = (s) => {
  if (!s) return -1;
  const idx = STATUS_ORDER.findIndex((k) => k.toLowerCase() === s.toLowerCase());
  return idx === -1 ? -1 : idx;
};

export default ({}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    received: "no",
    sent: "yes",
    limit: 400,
    sort: { dueAt: -1 },
    projectId: null,
    collection_status: [],
    organisation_id: null,
  });
  const [total, setTotal] = useState(0);
  const [organisations, setOrganisations] = useState([]);
  const { businessUnit } = useSelector((state) => state.Auth);
  // Post-cutoff defaults to on for Tirana (its books start Jan 2026) but is available everywhere.
  const [postCutoff, setPostCutoff] = useState(isTirana(businessUnit));
  const [overdueOnly, setOverdueOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    get();
  }, [filters, businessUnit._id, postCutoff, overdueOnly]);

  async function fetchOrganisations() {
    try {
      const { ok, data } = await api.post("/organisation/search");
      if (ok) setOrganisations(data);
    } catch (e) {
      console.log(e);
    }
  }

  async function get() {
    try {
      setLoading(true);

      const buFilter = businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id } : {};
      const cutoffFilter = postCutoff ? { dateFrom: TIRANA_CUTOFF } : {};
      const overdueFilter = overdueOnly ? { late_invoice: true } : {};

      const { ok, data, total } = await api.post(`/invoice/search`, {
        ...filters,
        ...buFilter,
        ...cutoffFilter,
        ...overdueFilter,
        sortBy: Object.keys(filters.sort)[0] + (Object.values(filters.sort)[0] === 1 ? "" : "-"),
      });

      setLoading(false);
      if (!ok) return toast.error("Something went wrong!");
      setData(data);
      setTotal(total);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  }

  if (!data) return <Loader />;

  const statuses = [...new Set(data.map((e) => e.collection_status))].sort((a, b) => statusRank(a) - statusRank(b));
  const activeStatusFilter = filters.collection_status?.length === 1 ? filters.collection_status[0].value : null;

  const toggleStatusChip = (status) => {
    const value = status || "Non défini";
    if (activeStatusFilter === value) return setFilters({ ...filters, collection_status: [], page: 1 });
    setFilters({ ...filters, collection_status: [{ value, label: value }], page: 1 });
  };

  const handleSort = (key) => {
    setFilters({ ...filters, sort: { [key]: !filters.sort[key] ? 1 : filters.sort[key] === 1 ? -1 : undefined } });
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header: what & how much on the left, navigation on the right */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            Recouvrement <span className="text-lg font-semibold text-gray-500">· {fmt(data.reduce((acc, item) => acc + (item.totalRemaining || 0), 0))} to collect</span>
          </h1>
          <Stats />
        </div>
        <ViewTabs />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-lg p-3">
        <div className="w-56">
          <label className="block text-xs font-medium mb-1 text-gray-500">Collection status</label>
          <MultiSelect
            values={filters?.collection_status || []}
            id="select-status"
            buttonClassName="px-3 py-2"
            options={[
              { value: "Non défini", label: "Non défini" },
              { value: "In progress", label: "In progress" },
              { value: "Late payment in progress", label: "Late payment in progress" },
              { value: "First reminder", label: "First reminder" },
              { value: "Second reminder + Call", label: "Second reminder + Call" },
              { value: "Third reminder", label: "Third reminder" },
              { value: "Mise en demeure to send", label: "Mise en demeure to send" },
              { value: "Mise en demeure sent", label: "Mise en demeure sent" },
              { value: "Mise en demeure received", label: "Mise en demeure received" },
              { value: "Fourth reminder", label: "Fourth reminder" },
              { value: "Injonction de payer to start", label: "Injonction de payer to start" },
              { value: "Injonction de payer sent", label: "Injonction de payer sent" },
            ]}
            onSelectedChange={(e) => setFilters({ ...filters, collection_status: e.length === 0 ? [] : e, page: 1 })}
            placeholder="All statuses"
          />
        </div>

        <div className="w-56">
          <label className="block text-xs font-medium mb-1 text-gray-500">Project</label>
          <SelectProject className="w-full" value={filters.projectId} onChange={(selected) => setFilters({ ...filters, projectId: selected ? selected._id : null, page: 1 })} />
        </div>

        <div className="w-56">
          <label className="block text-xs font-medium mb-1 text-gray-500">Company</label>
          <select
            value={filters.organisation_id || ""}
            onChange={(e) => setFilters({ ...filters, organisation_id: e.target.value || null, page: 1 })}
            className="block w-full border border-gray-200 rounded-md text-sm px-3 py-2 bg-white text-gray-700">
            <option value="">All</option>
            {organisations.map((o) => (
              <option key={o._id} value={o._id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 pb-2.5 text-xs text-gray-700 cursor-pointer" title="Only invoices dated Jan 1, 2026 or later — money Tirana is responsible for">
          <input type="checkbox" checked={postCutoff} onChange={(e) => setPostCutoff(e.target.checked)} className="rounded border-gray-300" />
          Post-cutoff only (≥ Jan 2026)
        </label>

        <label className="flex items-center gap-2 pb-2.5 text-xs text-gray-700 cursor-pointer" title="Only invoices whose due date has passed">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="rounded border-gray-300" />
          Overdue only
        </label>

        {(filters.collection_status?.length > 0 || filters.projectId || filters.organisation_id) && (
          <button
            className="text-xs text-gray-500 underline hover:text-gray-700 pb-2.5"
            onClick={() => setFilters({ ...filters, collection_status: [], projectId: null, organisation_id: null, page: 1 })}>
            Clear filters
          </button>
        )}
      </div>

      {/* Amount per status — click a chip to filter on it */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {statuses.map((e) => {
            const value = e || "Non défini";
            const isActive = activeStatusFilter === value;
            return (
              <button
                key={value}
                onClick={() => toggleStatusChip(e)}
                className={`px-2 py-1 text-sm rounded-md text-left cursor-pointer ${getStatusColor(e)} ${isActive ? "ring-2 ring-gray-500" : "hover:opacity-80"}`}>
                <div className="capitalize">{value}</div>
                <div className="font-semibold">{fmt(data.filter((item) => item.collection_status === e).reduce((acc, item) => acc + item.totalRemaining, 0))}</div>
              </button>
            );
          })}
        </div>
        <ColorLegend />
      </div>

      <Table
        header={HEADER}
        total={total}
        loading={loading}
        sort={filters.sort}
        onSort={handleSort}
        height="min-h-min"
        sticky={true}
        noResultRender={<div className="w-full bg-white text-center p-2 my-5 rounded shadow-sm">Aucune résultat n'a été trouvé</div>}>
        {loading
          ? [...Array(filters.limit)].map((_, index) => (
              <tr key={index} className={`text-center cursor-pointer ${index % 2 === 0 ? "bg-neutral-50" : "bg-white"}`}>
                {HEADER.map((_, index) => (
                  <td key={index} className="py-4 px-3 max-w-xs">
                    <div className="h-2 bg-gray-200 rounded w-full" />
                  </td>
                ))}
              </tr>
            ))
          : data.map((e, i) => {
              return (
                <tr
                  key={e.name + i}
                  onClick={(ev) => (ev.metaKey || ev.ctrlKey ? window.open(`/invoice/${e._id}/collection`, "_blank") : navigate(`/invoice/${e._id}/collection`))}
                  title="Open invoice collection"
                  className={`border-b border-gray-200 hover:bg-gray-100 cursor-pointer`}>
                  <td className="py-1 px-2">
                    <Link to={`/invoice/${e._id}/collection`} onClick={(ev) => ev.stopPropagation()} className="text-sm font-semibold text-left text-blue-600 hover:underline">
                      {invoiceNumber(e) || "N/A"}
                    </Link>
                  </td>
                  <td>
                    <div className="text-sm font-semibold text-left">
                      <Link to={`/project/${e.projectId}`} key={e.name} onClick={(ev) => ev.stopPropagation()} className="text-blue-600 hover:underline" title="Open project">
                        {e.projectName}
                      </Link>
                    </div>
                  </td>
                  <td className="py-1 px-2 text-sm whitespace-nowrap">{e.sentAt ? new Date(e.sentAt).toISOString().slice(0, 10) : ""}</td>
                  <td className="py-1 px-2 text-sm whitespace-nowrap">{e.dueAt ? new Date(e.dueAt).toISOString().slice(0, 10) : ""}</td>
                  <td className="py-1 px-2 text-sm whitespace-nowrap">{timeSinceShort(e.dueAt)}</td>
                  <td className="py-1 px-2 text-sm font-semibold">{e.total.toFixed(2)}€</td>
                  <td className="py-1 px-2">
                    {e.collection_status ? (
                      <span className={`px-2 py-1 font-semibold rounded-full text-xs whitespace-nowrap ${getStatusColor(e.collection_status)}`}>{e.collection_status}</span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-1 px-2 text-sm whitespace-nowrap capitalize">{e.nextActionDate ? new Date(e.nextActionDate).toISOString().slice(0, 10) : ""}</td>
                  <td className="py-1 px-2 text-sm whitespace-nowrap capitalize">{e.organisation_name}</td>
                </tr>
              );
            })}
        {!loading && data.length > 0 && (
          <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
            <td colSpan={5} className="py-2 px-2 text-sm text-left">
              Total — {data.length} invoices
            </td>
            <td className="py-2 px-2 text-sm whitespace-nowrap">{fmt(data.reduce((acc, e) => acc + (e.total || 0), 0))}</td>
            <td colSpan={3} className="py-2 px-2 text-xs text-gray-500 font-normal">
              of which {fmt(data.reduce((acc, e) => acc + (e.totalRemaining || 0), 0))} still to collect
            </td>
          </tr>
        )}
      </Table>
      <div className="mt-1">
        <Paginator page={filters.page} setPage={(page) => setFilters({ ...filters, page })} last={Math.ceil(total / filters.limit)} />
      </div>
    </div>
  );
};

const Stats = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {open && (
        <Modal isOpen={open} onClose={() => setOpen(false)} className="w-11/12">
          <div className=" w-full h-[90vh] bg-[white] p-[20px] rounded-md">
            <a href="https://selego-metabase.cleverapps.io/question/3261-invoice-amounts-not-received-by-month" target="_blank" rel="noopener noreferrer" className="pb-4">
              Ouvrir dans metabase
            </a>
            <iframe src="https://selego-metabase.cleverapps.io/public/question/dbbef414-7405-40e3-8abf-203e8bc54229" className="w-full h-full " title="Stats" />
          </div>
        </Modal>
      )}
      <IoStatsChart onClick={() => setOpen(true)} className="text-2xl text-primary-700 border border-gray-300 rounded-md p-1 cursor-pointer" />
    </>
  );
};
